"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2, X } from "lucide-react";

import { BindableInput } from "./BindableInput";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";
import {
  addMembershipItems,
  blankRow,
  canAddCondition,
  columnType,
  isBoundOperand,
  isFilterColumnType,
  legacyFiltersAsRows,
  isMembershipOperator,
  MAX_ADVANCED_CONDITIONS,
  MAX_MEMBERSHIP_ITEMS,
  membershipItems,
  operandCount,
  operandDataType,
  operandFromInput,
  operandToInput,
  operatorLabel,
  operatorsForColumnType,
  retainedLegacyFilters,
  serializeRows,
  withColumn,
  withOperator,
  type FilterColumnMeta,
  type FilterOperator,
  type MembershipItem,
  type TableFilterRow,
} from "./tableFilterRowState";
import { FileUpload } from "@/components/Form/FileInputField";
import { Combobox } from "@/components/shadcn/combobox";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import {
  NoxusFormLabel,
  PinFieldContext,
} from "@/components/Form/NoxusFormLabel";
import { DataTypeIcon } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";

/**
 * One typed operand control, chosen by the column's declared type: a datetime
 * picker, a True/False select, a number input, or — for File — the standard
 * file picker.
 *
 * The File case is why this is not a bare {@link BindableInput}. PRD 35 rules
 * out raw-URI text as an input mode (a mistyped URI matches nothing and reads
 * as a data problem), and PRD 65 says the picker resolves the chosen file to
 * its canonical Spot URI at save time — so the picker writes `file.uri`, and
 * clearing it writes nothing rather than `''` (PRD 66).
 */
function OperandInput({
  nodeId,
  columnType: type,
  inputName,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  nodeId: string;
  columnType: string;
  inputName: string;
  value: unknown;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const text = operandToInput(value);
  const isRef = WHOLE_VAR_REF_PATTERN.test(text);
  const filePicker =
    type === "file" ? (
      <FileUpload
        variant="inline"
        values={text && !isRef ? [{ uri: text, name: fileNameOf(text) }] : []}
        isDisabled={disabled}
        showSelectedFiles
        setValues={(files) => onChange(files[0]?.uri ?? null)}
      />
    ) : undefined;

  return (
    <BindableInput
      nodeId={nodeId}
      dataType={operandDataType(type)}
      strictTypes
      inputName={inputName}
      clearsToEmpty
      value={isRef || type !== "file" ? text : ""}
      placeholder={placeholder}
      disabled={disabled}
      renderLiteral={filePicker}
      onChange={(next) => onChange(operandFromInput(next, type))}
    />
  );
}

/** Last path segment of a Spot URI, so a picked file shows a name rather than
 * the whole URI. Display only — the persisted operand is always the full URI. */
function fileNameOf(uri: string): string {
  return uri.split("/").pop() || uri;
}

/**
 * The literal operand for `is any of` / `is none of`: a chip list held as one
 * operand, never as one condition per value (PRD 38).
 *
 * Purpose-built rather than reusing {@link ChipInput}, whose paste handler
 * splits on commas — the opposite of PRD 41, since a Text value may legitimately
 * contain one. All the entry rules live in `addMembershipItems` so they stay
 * testable without rendering; this component only routes Enter, paste and blur
 * into it.
 */
function MembershipChips({
  items,
  columnType: type,
  disabled,
  onChange,
}: {
  items: MembershipItem[];
  columnType: string;
  disabled?: boolean;
  onChange: (next: MembershipItem[]) => void;
}) {
  const [text, setText] = useState("");
  const atCap = items.length >= MAX_MEMBERSHIP_ITEMS;

  const commit = (raw: string) => {
    setText("");
    const next = addMembershipItems(items, raw, type);
    if (next.length !== items.length) onChange(next);
  };

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          "flex min-h-8 w-full flex-wrap items-center gap-1.5 rounded-md border",
          "border-border px-2 py-1 shadow-xs focus-within:ring-1 focus-within:ring-ring",
          disabled && "cursor-not-allowed opacity-50"
        )}
      >
        {items.map((item, index) => (
          <Badge key={`${index}-${item}`} variant="secondary">
            {String(item)}
            <Button
              type="button"
              size="iconxs"
              variant="subtle"
              disabled={disabled}
              aria-label={`Remove ${item}`}
              className="ml-1 size-2 bg-transparent"
              onClick={() => onChange(items.filter((_, i) => i !== index))}
            >
              <X className="size-2" strokeWidth="1.33px" />
            </Button>
          </Badge>
        ))}
        <Input
          aria-label="Filter values"
          value={text}
          disabled={disabled || atCap}
          placeholder={items.length ? undefined : "Type a value, then Enter"}
          className={cn(
            "h-6 min-w-[8rem] flex-grow border-none! px-0 py-0 shadow-none",
            "focus-visible:border-none! focus-visible:ring-0!"
          )}
          onChange={(e) => setText(e.target.value)}
          // Committing on blur too, so a typed value the user never confirmed
          // with Enter is not silently thrown away when they click elsewhere.
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && text !== "") {
              e.preventDefault();
              commit(text);
            }
            if (e.key === "Backspace" && text === "" && items.length > 0) {
              e.preventDefault();
              onChange(items.slice(0, -1));
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            commit(text + e.clipboardData.getData("text"));
          }}
        />
      </div>
      {atCap ? (
        <NoxusFormHelperText>
          {`Limit of ${MAX_MEMBERSHIP_ITEMS} values reached.`}
        </NoxusFormHelperText>
      ) : null}
    </div>
  );
}

/**
 * The membership operand: either one literal chip list or one upstream list
 * (PRD 37), never both.
 *
 * `isList` is what makes the two mutually exclusive. It narrows the picker to
 * list-typed outputs of the column's element type, so a scalar can't be bound
 * here in the first place (PRD 39) — the backend still rejects one, but only
 * after the run has started. And once a variable *is* bound, `BindableInput`
 * swaps its whole shell for the variable chip, so the chip editor disappears
 * without this component deciding anything (PRD 43).
 */
function MembershipOperand({
  nodeId,
  columnType: type,
  inputName,
  value,
  disabled,
  onChange,
}: {
  nodeId: string;
  columnType: string;
  inputName: string;
  value: unknown;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const bound = isBoundOperand(value);
  return (
    <BindableInput
      nodeId={nodeId}
      dataType={operandDataType(type)}
      strictTypes
      isList
      inputName={inputName}
      value={bound ? String(value) : ""}
      disabled={disabled}
      renderLiteral={
        <MembershipChips
          items={membershipItems(value)}
          columnType={type}
          disabled={disabled}
          onChange={onChange}
        />
      }
      // Clearing the binding hands back `""`, which is not a list — reset to an
      // empty chip list so the operand keeps the shape its operator requires.
      onChange={(next) => onChange(next === "" ? [] : next)}
    />
  );
}

/**
 * V2 renderer for `display=ConfigTableFilterRows` — Export Table's advanced
 * filters. Each row is a column picked from live table metadata, an operator
 * restricted to that column's declared type, and the operands that operator
 * requires. Rows combine with AND; the same column may appear more than once.
 *
 * `columns` comes from the node's `get_config` (already stripped of `id` and
 * `reference`), so the editor never hard-codes a column or type list. All row
 * transition rules live in {@link tableFilterRowState} so they stay testable
 * without rendering, and so this editor cannot persist a shape the backend's
 * `validate_advanced_filters` would reject.
 */
export function TableFilterRowsField({
  nodeId,
  fieldKey,
  label,
  columns,
  description,
  addLabel,
  emptyMessage,
  disabled,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label?: string;
  columns?: unknown;
  description?: string;
  addLabel?: string;
  emptyMessage?: string;
  disabled?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as TableFilterRow[] | undefined;
  const rows: TableFilterRow[] = useMemo(
    () => (Array.isArray(raw) ? raw : []),
    [raw]
  );
  const meta: FilterColumnMeta[] = useMemo(
    () => (Array.isArray(columns) ? (columns as FilterColumnMeta[]) : []),
    [columns]
  );
  const invalid = !!error;

  // A row with no column picked is a draft: rendered so the user can fill it
  // in, but never persisted (PRD 17) — the config holds only real conditions.
  // Drafts therefore live here rather than in the form value, and the form is
  // re-read only when it changes from outside (node reload, undo), which a
  // local edit never triggers because `write` stores what it just sent.
  // Configurations saved before the two filter editors merged keep their
  // equality conditions in a separate map. They are shown here, ahead of the
  // rest, so an old node presents one list rather than filtering by rules with
  // no control — and they are moved across on the first real edit, not on mere
  // open, so looking at a node never dirties it.
  const legacyPath = `${nodeId}.filters`;
  const legacyRaw = form.watch(legacyPath) as unknown;
  const legacyRows = useMemo(
    () => legacyFiltersAsRows(legacyRaw, meta),
    [legacyRaw, meta]
  );
  const initial = useMemo(() => [...legacyRows, ...rows], [legacyRows, rows]);
  // What the conditions list cannot take over. `_filter_specs` still applies
  // these at run time, so they are named rather than left to filter invisibly.
  const retainedLegacy = useMemo(
    () => retainedLegacyFilters(legacyRaw, meta),
    [legacyRaw, meta]
  );
  const retainedLegacyColumns = Object.keys(retainedLegacy);

  const [draft, setDraft] = useState<TableFilterRow[]>(initial);
  const lastWritten = useRef<string>(JSON.stringify(serializeRows(initial)));

  useEffect(() => {
    const incoming = JSON.stringify(serializeRows(initial));
    if (incoming === lastWritten.current) return;
    lastWritten.current = incoming;
    setDraft(initial);
  }, [initial]);

  const write = (next: TableFilterRow[]) => {
    setDraft(next);
    const serialized = serializeRows(next);
    // The legacy map had no cap, so a wide table can arrive with more equality
    // filters than `validate_advanced_filters` accepts. Persisting the
    // migration here would replace a config that exports today with one that
    // fails validation — and writing only the first 20 would drop filters and
    // change the result. So nothing is persisted while the list is over the
    // cap: the legacy map stays authoritative, and therefore still applied,
    // until the user trims the draft to fit.
    if (serialized.length > MAX_ADVANCED_CONDITIONS) return;
    lastWritten.current = JSON.stringify(serialized);
    form.setValue(path, serialized, { shouldDirty: true, shouldTouch: true });
    // The migrated conditions now live in `serialized`; leaving those entries in
    // the legacy map would apply each of them twice. Only those, though: an `id`
    // filter, one on a non-filterable column, or anything at all when no schema
    // was available, has no condition to migrate into, and clearing it would
    // drop a filter `_filter_specs` is still applying.
    if (legacyRows.length > 0) {
      form.setValue(legacyPath, retainedLegacy, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const conditionCount = draft.filter((row) => row.field !== "").length;
  const overCap = conditionCount > MAX_ADVANCED_CONDITIONS;

  const patch = (index: number, next: TableFilterRow) =>
    write(draft.map((row, i) => (i === index ? next : row)));

  const columnOptions = meta.map((c) => ({
    value: c.name,
    label: c.label || c.name,
    icon: <DataTypeIcon dataType={c.type} />,
  }));

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <NoxusFormLabel
          label={label}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
        />
      ) : null}
      {description ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <PinFieldContext.Provider value={{}}>
        <div
          className={cn(
            "flex w-full flex-col gap-6 pr-3 pt-3",
            invalid && "rounded-md ring-1 ring-inset ring-amber-400/70 p-1.5"
          )}
        >
          {draft.map((row, index) => {
            const type = columnType(meta, row.field) ?? "";
            const operators = operatorsForColumnType(type);
            const arity = operandCount(row.op);
            // No schema for this column — the table is bound to a variable, or
            // the config endpoint has not answered. Its operators and operand
            // type are unknowable, and the fallbacks are Text ones: editing the
            // row would rewrite a saved Number operand as a string and break a
            // condition that was valid. Show it, allow removing it, edit nothing.
            const unknownType = row.field !== "" && !isFilterColumnType(type);
            const frozen = disabled || unknownType;
            // Combobox falls back to its placeholder for a value absent from its
            // options, so without a schema a live condition renders as an
            // untouched blank row. Its saved column and operator become the only
            // options — the row is frozen, so nothing else could be picked.
            const rowColumnOptions = unknownType
              ? [{ value: row.field, label: row.field }]
              : columnOptions;
            const rowOperators = operators.includes(row.op)
              ? operators
              : [row.op];
            return (
              <div key={index} className="relative w-full">
                {index > 0 ? (
                  <div className="absolute -top-3 left-4 z-10 -translate-y-1/2 rounded bg-background px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                    AND
                  </div>
                ) : null}
                <div className="absolute -right-3 -top-3 z-10 rounded-full bg-background">
                  <Button
                    type="button"
                    variant="outline"
                    size="iconxs"
                    disabled={disabled}
                    aria-label="Remove filter"
                    className="rounded-full text-muted-foreground"
                    onClick={() => write(draft.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="size-3" strokeWidth={1.25} />
                  </Button>
                </div>
                <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
                  <div className="flex flex-col gap-1.5">
                    <NoxusFormLabel
                      label="Column"
                      noMargin
                      isDisabled={disabled}
                    />
                    <div className={cn(row.field && "grid grid-cols-2 gap-2")}>
                      <Combobox
                        ariaLabel="Filter column"
                        options={rowColumnOptions}
                        value={row.field || undefined}
                        placeholder="Select column…"
                        searchPlaceholder="Search columns…"
                        emptyText="No columns available"
                        onValueChange={(next) => {
                          if (next) patch(index, withColumn(row, next, meta));
                        }}
                        disabled={disabled}
                        hasPortal
                        className="h-8 w-full min-w-0"
                      />
                      {row.field ? (
                        <Combobox
                          ariaLabel="Filter operator"
                          options={rowOperators.map((op) => ({
                            value: op,
                            label: operatorLabel(op),
                          }))}
                          value={row.op}
                          placeholder="Select operator…"
                          emptyText="No operators for this column type"
                          searchable={false}
                          onValueChange={(next) => {
                            if (next)
                              patch(
                                index,
                                withOperator(row, next as FilterOperator)
                              );
                          }}
                          disabled={frozen}
                          hasPortal
                          className="h-8 w-full min-w-0"
                        />
                      ) : null}
                    </div>
                  </div>
                  {row.field && arity >= 1 ? (
                    <div className="flex flex-col gap-1.5">
                      <NoxusFormLabel
                        label="Value"
                        noMargin
                        isDisabled={disabled}
                      />
                      {isMembershipOperator(row.op) ? (
                        <MembershipOperand
                          nodeId={nodeId}
                          columnType={type}
                          inputName={`${fieldKey}.${index}.value`}
                          value={row.value}
                          disabled={frozen}
                          onChange={(next) =>
                            patch(index, { ...row, value: next })
                          }
                        />
                      ) : (
                        <OperandInput
                          nodeId={nodeId}
                          columnType={type}
                          inputName={`${fieldKey}.${index}.value`}
                          value={row.value}
                          placeholder={arity === 2 ? "From" : undefined}
                          disabled={frozen}
                          onChange={(next) =>
                            patch(index, { ...row, value: next })
                          }
                        />
                      )}
                      {!isMembershipOperator(row.op) && arity >= 2 ? (
                        <OperandInput
                          nodeId={nodeId}
                          columnType={type}
                          inputName={`${fieldKey}.${index}.value2`}
                          value={row.value2}
                          placeholder="To"
                          disabled={frozen}
                          onChange={(next) =>
                            patch(index, { ...row, value2: next })
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </PinFieldContext.Provider>
      {meta.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          {emptyMessage ?? "Pick a table to filter by its columns."}
        </p>
      ) : null}
      {/* The same `pr-3` the card list carries, so the button lines up with the
          cards rather than overhanging them. That inset exists because each
          card's remove button sits `-right-3` outside its edge. */}
      <div className={cn("pr-3", draft.length > 0 && "mt-2")}>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || meta.length === 0 || !canAddCondition(draft)}
          className="w-full"
          onClick={() => write([...draft, blankRow()])}
        >
          {addLabel ?? "Add filter"}
          <Plus size="1.25rem" strokeWidth="1.25px" />
        </Button>
      </div>
      {retainedLegacyColumns.length > 0 ? (
        <Banner
          label={`Filters saved earlier (${retainedLegacyColumns.join(
            ", "
          )}) still apply to this export and cannot be edited here.`}
          style="info"
          size="sm"
        />
      ) : null}
      {overCap ? (
        <Banner
          label={`An export accepts at most ${MAX_ADVANCED_CONDITIONS} filters; this one has ${conditionCount}. Remove ${
            conditionCount - MAX_ADVANCED_CONDITIONS
          } to save these changes. Until then the filters saved earlier stay in force.`}
          style="warning"
          size="sm"
        />
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}
